import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, CheckCircle, XCircle, Trash2, UserPlus, Eye, EyeOff, KeyRound, Copy, Check } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import { authFetch } from "../utils/auth";
import UserProfileMenu from "./UserProfileMenu";

const MASTER_ADMIN = "admin";

const ROLE_LABELS = {
  user: "Enterprise User",
  support_agent: "Support Agent",
  agent: "Agent",
};

const statusColor = (enabled, status) => {
  if (!enabled) return "bg-red-100 text-red-600";
  if (status === "CONFIRMED") return "bg-green-100 text-green-700";
  if (status === "UNCONFIRMED") return "bg-yellow-100 text-yellow-700";
  if (status === "FORCE_CHANGE_PASSWORD") return "bg-orange-100 text-orange-700";
  return "bg-gray-100 text-gray-500";
};

const statusLabel = (enabled, status) => {
  if (!enabled) return "Disabled";
  if (status === "CONFIRMED") return "Active";
  if (status === "UNCONFIRMED") return "Pending";
  if (status === "FORCE_CHANGE_PASSWORD") return "Password Reset Required";
  return status || "Unknown";
};

const UserManagementPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [pendingAdmin, setPendingAdmin] = useState({}); // { username: bool }
  const [confirmDelete, setConfirmDelete] = useState(null); // username to delete

  // Add User modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ username: "", email: "", password: "", role: "support_agent", is_admin: false });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState("");
  const [showAddPassword, setShowAddPassword] = useState(false);

  // Reset password state
  const [resetConfirm, setResetConfirm] = useState(null); // username pending confirmation
  const [resetResult, setResetResult] = useState(null); // { username, temp_password }
  const [resetLoading, setResetLoading] = useState({});
  const [resetCopied, setResetCopied] = useState(false);

  const generatePassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ", lower = "abcdefghijklmnopqrstuvwxyz", digit = "0123456789", special = "!@#$%^&*";
    const all = upper + lower + digit + special;
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    const base = [pick(upper), pick(lower), pick(digit), pick(special)];
    for (let i = 0; i < 8; i++) base.push(pick(all));
    return base.sort(() => Math.random() - 0.5).join("");
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/admin/users");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to fetch users.");
      }
      const data = await res.json();
      const list = (data.users || []).filter((u) => u.status !== "UNCONFIRMED");
      setUsers(list);
      // Init pending admin state from current roles
      const initAdmin = {};
      list.forEach((u) => { initAdmin[u.username] = (u.roles || []).includes("admin"); });
      setPendingAdmin(initAdmin);
    } catch (err) {
      setError(err.message || "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const postAction = async (username, endpoint, body, successText) => {
    setActionLoading((prev) => ({ ...prev, [username]: endpoint }));
    setError("");
    setSuccessMsg("");
    try {
      const res = await authFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detail = b.detail;
        let msg = "Action failed.";
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
        else if (detail && typeof detail === "object" && detail.message) msg = String(detail.message);
        throw new Error(msg);
      }
      setSuccessMsg(successText);
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [username]: null }));
    }
  };

  const handleToggleEnable = (user) => {
    const endpoint = user.enabled ? "/api/admin/disable-user" : "/api/admin/enable-user";
    const msg = user.enabled
      ? `${user.username} has been disabled.`
      : `${user.username} has been enabled.`;
    postAction(user.username, endpoint, { username: user.username }, msg);
  };

  const handleToggleAdmin = (username) => {
    setPendingAdmin((prev) => ({ ...prev, [username]: !prev[username] }));
  };

  const handleSaveAdmin = (user) => {
    const currentRoles = user.roles || [];
    const wantsAdmin = pendingAdmin[user.username];
    const newRoles = wantsAdmin
      ? [...currentRoles.filter((r) => r !== "admin"), "admin"]
      : currentRoles.filter((r) => r !== "admin");
    postAction(
      user.username,
      "/api/admin/update-user-roles",
      { username: user.username, roles: newRoles },
      wantsAdmin ? `Admin granted to ${user.username}.` : `Admin removed from ${user.username}.`
    );
  };

  const adminChanged = (user) => {
    const current = (user.roles || []).includes("admin");
    return pendingAdmin[user.username] !== undefined && pendingAdmin[user.username] !== current;
  };

  const handleDeleteConfirmed = async () => {
    const username = confirmDelete;
    setConfirmDelete(null);
    setActionLoading((prev) => ({ ...prev, [username]: "delete" }));
    setError("");
    setSuccessMsg("");
    try {
      const res = await authFetch(`/api/admin/users/${encodeURIComponent(username)}`, { method: "DELETE" });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to delete user.");
      }
      setSuccessMsg(`${username} has been permanently deleted.`);
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to delete user.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [username]: null }));
    }
  };

  const handleResetPassword = async (username) => {
    setResetLoading((prev) => ({ ...prev, [username]: true }));
    setError("");
    try {
      const res = await authFetch("/api/admin/reset-user-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to reset password.");
      }
      const data = await res.json();
      setResetResult({ username: data.username, temp_password: data.temp_password });
      setResetCopied(false);
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to reset password.");
    } finally {
      setResetLoading((prev) => ({ ...prev, [username]: false }));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError("");
    const { username, email, password, role, is_admin } = addUserForm;
    if (!username.trim()) { setAddUserError("Username is required."); return; }
    if (users.some((u) => u.username.toLowerCase() === username.trim().toLowerCase())) {
      setAddUserError(`Username "${username.trim()}" already exists. Please choose a different username.`);
      return;
    }
    if (users.some((u) => u.email && u.email.toLowerCase() === email.trim().toLowerCase())) {
      setAddUserError(`Email "${email.trim()}" is already in use by another account.`);
      return;
    }
    if (!email.trim() || !email.includes("@")) { setAddUserError("A valid email is required."); return; }
    if (password.length < 8) { setAddUserError("Password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(password)) { setAddUserError("Password must contain at least 1 uppercase letter."); return; }
    if (!/[0-9]/.test(password)) { setAddUserError("Password must contain at least 1 number."); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setAddUserError("Password must contain at least 1 special character."); return; }

    setAddUserLoading(true);
    try {
      const res = await authFetch("/api/admin/create-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), email: email.trim().toLowerCase(), password, roles: [role], is_admin }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detail = b.detail;
        let msg = "Failed to create user.";
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
        throw new Error(msg);
      }
      const data = await res.json();
      setShowAddUser(false);
      setAddUserForm({ username: "", email: "", password: "", role: "support_agent", is_admin: false });
      setShowAddPassword(false);
      setSuccessMsg(data.message || `User '${username}' created successfully.`);
      await fetchUsers();
    } catch (err) {
      setAddUserError(err.message || "Failed to create user.");
    } finally {
      setAddUserLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main */}
      <div className="flex-1 relative flex flex-col px-4 sm:px-6 lg:px-8 py-8 overflow-auto">
        <div className="w-full">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">User Management</h1>
              <p className="text-gray-500 text-sm mt-1">Enable, disable, or update roles for all users.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setAddUserError(""); setAddUserForm({ username: "", email: "", password: generatePassword(), role: "support_agent", is_admin: false }); setShowAddPassword(true); setShowAddUser(true); }}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-[#253439] px-4 py-2 rounded-lg text-sm font-semibold border border-[#EBECE6] transition"
              >
                <UserPlus size={16} />
                Add User
              </button>
              <button
                onClick={fetchUsers}
                disabled={loading}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
              <UserProfileMenu onLogout={onLogout} />
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          {successMsg && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{successMsg}</div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No users found.</div>
          ) : (
            <div className="border border-[#EBECE6] rounded-xl overflow-visible bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f6f6f1] text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">Username</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Account Type</th>
                    <th className="px-4 py-3 font-semibold text-center">Admin</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isMaster = user.username === MASTER_ADMIN;
                    const isBusy = !!actionLoading[user.username];
                    const roles = user.roles || [];
                    const baseRoles = roles.filter((r) => r !== "admin");
                    const pendingChecked = pendingAdmin[user.username] ?? roles.includes("admin");
                    const isDirty = adminChanged(user);

                    return (
                      <tr
                        key={user.username}
                        className={`border-t border-[#EBECE6] transition ${!user.enabled ? "opacity-60" : "hover:bg-[#fafaf8]"}`}
                      >
                        {/* Username */}
                        <td className="px-4 py-3 font-medium text-[#253439]">{user.username}</td>

                        {/* Email */}
                        <td className="px-4 py-3 text-gray-500 text-xs">{user.email || "—"}</td>

                        {/* Account Type — read-only badges */}
                        <td className="px-4 py-3">
                          {isMaster ? (
                            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">Admin (permanent)</span>
                          ) : baseRoles.length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {baseRoles.map((r) => (
                                <span key={r} className="inline-block text-xs font-semibold px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">
                                  {ROLE_LABELS[r] || r}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        {/* Admin checkbox + Save */}
                        <td className="px-4 py-3 text-center">
                          {isMaster ? (
                            <input type="checkbox" checked disabled className="w-4 h-4 accent-cyan-500 cursor-not-allowed" />
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="checkbox"
                                checked={pendingChecked}
                                disabled={isBusy}
                                onChange={() => handleToggleAdmin(user.username)}
                                className="w-4 h-4 accent-cyan-500 cursor-pointer disabled:cursor-not-allowed"
                              />
                              {isDirty && (
                                <button
                                  type="button"
                                  onClick={() => handleSaveAdmin(user)}
                                  disabled={isBusy}
                                  className="text-xs bg-cyan-500 hover:bg-cyan-600 text-white px-2 py-0.5 rounded disabled:opacity-50"
                                >
                                  {isBusy ? <RefreshCw size={11} className="animate-spin" /> : "Save"}
                                </button>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor(user.enabled, user.status)}`}>
                            {statusLabel(user.enabled, user.status)}
                          </span>
                        </td>

                        {/* Actions: Enable/Disable + Delete */}
                        <td className="px-4 py-3 text-right">
                          {(isMaster) ? (
                            <span className="text-xs text-gray-400 italic">Protected</span>
                          ) : (
                            <div className="inline-flex items-center gap-2">
                              <button
                                onClick={() => handleToggleEnable(user)}
                                disabled={isBusy}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-50 ${user.enabled
                                  ? "border-red-200 text-red-600 hover:bg-red-50"
                                  : "border-green-200 text-green-700 hover:bg-green-50"
                                  }`}
                              >
                                {isBusy && actionLoading[user.username] !== "delete" ? (
                                  <RefreshCw size={13} className="animate-spin" />
                                ) : user.enabled ? (
                                  <XCircle size={13} />
                                ) : (
                                  <CheckCircle size={13} />
                                )}
                                {user.enabled ? "Disable" : "Enable"}
                              </button>
                              <button
                                onClick={() => setResetConfirm(user.username)}
                                disabled={isBusy || resetLoading[user.username]}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
                                title="Reset user password"
                              >
                                {resetLoading[user.username] ? <RefreshCw size={13} className="animate-spin" /> : <KeyRound size={13} />}
                                Reset Password
                              </button>
                              <button
                                onClick={() => setConfirmDelete(user.username)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                                title="Permanently delete user"
                              >
                                {isBusy && actionLoading[user.username] === "delete" ? (
                                  <RefreshCw size={13} className="animate-spin" />
                                ) : (
                                  <Trash2 size={13} />
                                )}
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Reset Password confirmation modal */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-cyan-100 rounded-full p-2">
                <KeyRound size={20} className="text-cyan-600" />
              </div>
              <h2 className="text-lg font-bold text-[#253439]">Reset Password?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              You are about to reset the password for <span className="font-semibold text-[#253439]">{resetConfirm}</span>.
            </p>
            <p className="text-sm text-gray-500 mb-5">
              A new temporary password will be generated. The user will be required to set a new password on their next login.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setResetConfirm(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const username = resetConfirm;
                  setResetConfirm(null);
                  await handleResetPassword(username);
                }}
                disabled={resetLoading[resetConfirm]}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white transition disabled:opacity-50 flex items-center gap-2"
              >
                {resetLoading[resetConfirm] ? <RefreshCw size={14} className="animate-spin" /> : <KeyRound size={14} />}
                Yes, Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password result modal */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-cyan-100 rounded-full p-2">
                <KeyRound size={20} className="text-cyan-600" />
              </div>
              <h2 className="text-lg font-bold text-[#253439]">Password Reset</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              A temporary password has been set for <span className="font-semibold text-[#253439]">{resetResult.username}</span>.
            </p>
            <p className="text-sm text-gray-500 mb-4">Share this with the user, they will be prompted to set a new password on next login.</p>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-5">
              <code className="flex-1 font-mono text-sm text-[#253439] tracking-wider">{resetResult.temp_password}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); setResetCopied(true); }}
                className="text-gray-400 hover:text-cyan-600 transition flex-shrink-0"
                title="Copy to clipboard"
              >
                {resetCopied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setResetResult(null); setResetCopied(false); }}
                className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setResetResult(null); setResetCopied(false); }}
                className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User modal */}
      {showAddUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="bg-cyan-100 rounded-full p-2">
                  <UserPlus size={20} className="text-cyan-600" />
                </div>
                <h2 className="text-lg font-bold text-[#253439]">Add New User</h2>
              </div>
              <button
                type="button"
                onClick={() => { setShowAddUser(false); setAddUserError(""); setShowAddPassword(false); }}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none bg-transparent border-none cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddUser} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  placeholder="e.g. john.doe"
                  value={addUserForm.username}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, username: e.target.value }))}
                  autoFocus
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  placeholder="john.doe@company.com"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>

              {/* Temporary Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
                <div className="relative">
                  <input
                    type={showAddPassword ? "text" : "password"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 font-mono"
                    value={addUserForm.password}
                    onChange={(e) => setAddUserForm((f) => ({ ...f, password: e.target.value }))}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowAddPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showAddPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Auto-generated. User will be prompted to set a new password on first login.</p>
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                  value={addUserForm.role}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="support_agent">Support Agent</option>
                  <option value="user">Enterprise User</option>
                </select>
              </div>

              {/* Admin rights */}
              <div className="flex items-center gap-3 py-1">
                <input
                  type="checkbox"
                  id="add-user-admin"
                  className="w-4 h-4 accent-cyan-500 cursor-pointer"
                  checked={addUserForm.is_admin}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, is_admin: e.target.checked }))}
                />
                <label htmlFor="add-user-admin" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  Grant admin rights
                </label>
              </div>

              {addUserError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{addUserError}</div>
              )}

              <div className="flex gap-3 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => { setShowAddUser(false); setAddUserError(""); setShowAddPassword(false); }}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addUserLoading}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white transition disabled:opacity-50 flex items-center gap-2"
                >
                  {addUserLoading ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
                  {addUserLoading ? "Creating…" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-red-100 rounded-full p-2">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-[#253439]">Delete user?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              You are about to permanently delete <span className="font-semibold text-[#253439]">{confirmDelete}</span>.
            </p>
            <p className="text-sm text-red-600 font-medium mb-5">
              ⚠ This cannot be undone. The account will be removed permanently.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirmed}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white transition"
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
